import i18n from 'i18n-js';
import moment from 'moment';
import { TimePeriod } from '@v2/pages/machines/common/enums';
import { getEightWeeksWindow, getPeriodStep } from '@components/machines/components/eventsChart/helpers/period';
import { BAR_STEP_UNIT } from './constants';

const formatBarDateLabel = (barDate, selectedTime, index) => {
  const dayLabel = `${barDate.format('DD')} ${i18n.t(barDate.format('MMM'))} ${barDate.format('YYYY')}`;
  switch (selectedTime) {
    case TimePeriod.DAY:
      return `${dayLabel} ${checkNumber(index)}${i18n.t('h')}-${checkNumber(index + 1)}${i18n.t('h')}`;
    case TimePeriod.EIGHT_WEEKS:
      return `${i18n.t('Week')} ${barDate.isoWeek()} ${barDate.isoWeekYear()}`;
    default:
      return dayLabel;
  }
};

export const formatDateToView = (d, selectedTime) => {
  switch (selectedTime) {
    case TimePeriod.DAY: {
      const month = i18n.t(moment(d).format('MMM'));
      const isToday = moment(d).isSame(moment().startOf('day'), 'd');
      if (isToday) {
        return `${i18n.t('Today')} ${moment(d).format('DD')} ${month}`;
      }
      const isYesterday = moment(d).isSame(moment().subtract(1, 'days').startOf('day'), 'd');
      if (isYesterday) {
        return `${i18n.t('Yesterday')} ${moment(d).format('DD')} ${month}`;
      }

      const isSameYear = moment(d).isSame(moment(), 'year');

      return `${i18n.t(moment(d).format('dddd'))} ${moment(d).format('DD')} ${month} ${isSameYear ? '' : moment(d).format('YYYY')}`;
    }
    case TimePeriod.WEEK: {
      const start = moment(d).startOf('isoWeek');
      const end = moment(d).endOf('isoWeek');
      const monthStart = i18n.t(moment(start).format('MMM'));
      const monthEnd = i18n.t(moment(end).format('MMM'));

      return `${start.format('DD')} ${monthStart} - ${end.format('DD')} ${monthEnd}`;
    }
    case TimePeriod.MONTH: {
      const start = moment(d).startOf('month');
      const end = moment(d).endOf('month');
      const monthStart = i18n.t(moment(start).format('MMM'));
      const monthEnd = i18n.t(moment(end).format('MMM'));

      return `${start.format('DD')} ${monthStart} - ${end.format('DD')} ${monthEnd}`;
    }
    case TimePeriod.YEAR: {
      const start = moment(d).startOf('year');
      const end = moment(d).endOf('year');
      const monthStart = i18n.t(moment(start).format('MMM'));
      const monthEnd = i18n.t(moment(end).format('MMM'));

      return `${start.format('DD')} ${monthStart} - ${end.format('DD')} ${monthEnd}`;
    }
    case TimePeriod.EIGHT_WEEKS: {
      const { start, end } = getEightWeeksWindow(moment(d));
      const lastWeek = end.clone().subtract(1, 'isoWeek');
      return `${i18n.t('Week')} ${start.isoWeek()}-${lastWeek.isoWeek()} ${lastWeek.isoWeekYear()}`;
    }
  }
};

// Get OEE effectiveness label based on percentage
export const getOEEEffectivenessLabel = (oeeValue) => {
  if (oeeValue == null) return null;
  const percent = oeeValue * 100;
  if (percent < 40) return { label: i18n.t('Poor'), color: 'OEE/Light/Score Poor' };
  if (percent < 60) return { label: i18n.t('Fair'), color: 'OEE/Light/Score Fair' };
  if (percent < 75) return { label: i18n.t('Good'), color: 'OEE/Light/Score Good' };
  if (percent < 85) return { label: i18n.t('Excellent'), color: 'OEE/Light/Score Excellent' };
  return { label: i18n.t('World Class'), color: 'OEE/Light/Score World Class' };
};

// Format bar date for popup in utilization chart
const checkNumber = (n) => (n < 10 ? `0${n}` : n);

export const formatBarData = (data, chartDate, selectedTime) => {
  const { unit: startUnit } = getPeriodStep(selectedTime);
  const startDate =
    selectedTime === TimePeriod.EIGHT_WEEKS ? getEightWeeksWindow(moment(chartDate)).start : moment(chartDate).startOf(startUnit);
  const stepUnit = BAR_STEP_UNIT[selectedTime] || 'days';

  // SF-5106: backend's machineStatsWeekOeeTeep can omit weeks where the machine
  // has no rows yet (e.g. demo machine pre-dating an early week). Indexing bars
  // positionally would shift the rest left and leave the LAST slot empty, so
  // align each item to its real ISO-week Monday.
  if (selectedTime === TimePeriod.EIGHT_WEEKS) {
    const byMonday = new Map((data || []).map((item) => [moment(item.date).startOf('isoWeek').format('YYYY-MM-DD'), item]));
    return Array.from({ length: 8 }, (_, index) => {
      const barDate = startDate.clone().add(index, stepUnit);
      const item = byMonday.get(barDate.format('YYYY-MM-DD')) || {};
      return {
        ...item,
        date: formatBarDateLabel(barDate, selectedTime, index),
        dateISO: barDate.format(),
      };
    });
  }

  return (data || []).map((item, index) => {
    const barDate = startDate.clone().add(index, stepUnit);
    return {
      ...item,
      date: formatBarDateLabel(barDate, selectedTime, index),
      dateISO: barDate.format(),
    };
  });
};
